/**
 * Copy for step 2's Scenario picker and the four Scenario panels, transcribed from Figma
 * (RM-Operator AZmAouUC6TczcPKPOiaAoy, section 11724:214876 "Solo Operator (Step 2 States)").
 *
 * Two deliberate deviations from the frames are marked inline: the Scenario 3 FAQ typo and the
 * FAQ subsets for Scenarios 1, 2 and 4, which Figma never drew.
 */

export type SoloScenarioId = "transfer" | "sign-in" | "register" | "branded";

/** Placeholder in the Figma copy; replaced at render time with the operator's portal login email. */
export const OPERATOR_EMAIL_PLACEHOLDER = "[operator email]";

/** A run of copy. A plain string is regular weight; the object form carries emphasis. */
export type CopyRun = string | { text: string; bold?: boolean; underline?: boolean };

export type RichText = CopyRun[];

export interface SoloScenarioAnswer {
  id: SoloScenarioId;
  title: string;
  outcome: string;
}

export interface SoloScenarioChecklistItem {
  title: string;
  description: RichText;
}

export interface SoloScenarioFaqItem {
  symptom: string;
  fix: string;
}

export interface SoloScenario {
  id: SoloScenarioId;
  /** Scenario 4 only: sets up why a branded account does not carry over. */
  intro?: RichText[];
  checklist: SoloScenarioChecklistItem[];
  steps: RichText[];
  /** Absent for Scenario 3, which has nothing to warn about. */
  warning?: { title: string; body: RichText };
  faq: SoloScenarioFaqItem[];
}

export const scenarioAnswers: SoloScenarioAnswer[] = [
  {
    id: "transfer",
    title: "Yes, and I drive for another operator",
    outcome: "You'll transfer that account across to this operator",
  },
  {
    id: "sign-in",
    title: "Yes, but I'm not attached to an operator",
    outcome: "You'll sign in with that account and join your own Solo",
  },
  {
    id: "register",
    title: "No, I've never had one",
    outcome: "You'll create one. It needs its own email address",
  },
  {
    id: "branded",
    title: "I drive on COD / Royale's own branded app",
    outcome: "You'll be starting fresh here",
  },
];

export const scenarioHint =
  "Not sure? Look at the app you drive with now. If the icon and sign-in screen show COD / Royale's name and logo rather than RideMinder's, pick the last one.";

/**
 * Figma only drew the FAQ list for Scenario 3 (frame 11724:215733). The subsets below reuse those
 * items verbatim, narrowed to the failures each Scenario can actually hit — a dev decision pending
 * Jodie's sign-off on DSN-604.
 */
const faqWrongEmail: SoloScenarioFaqItem = {
  symptom: "“The email is already in use”.",
  // Figma reads "ou entered"; corrected here rather than shipping the typo.
  fix: "You entered your operator login in the Driver App. Your driver account needs a different email address. Go back and sign up with another one.",
};

const faqWrongOperator: SoloScenarioFaqItem = {
  symptom: "The app opened inside another operator.",
  fix: "Your driver account is still attached to another operator. In the app, open the menu → profile (scroll all the way down) → Detach from Operator, then come back here.",
};

const faqMissingCode: SoloScenarioFaqItem = {
  symptom: "The app asked for a code and I don't have it.",
  fix: "It's the Transport Provider Code above.",
};

export const stillStuckSuffix = " — we can see your setup and help you fix it.";

export const soloScenarios: Record<SoloScenarioId, SoloScenario> = {
  transfer: {
    id: "transfer",
    checklist: [
      {
        title: "Your existing Driver App login",
        description: ["The one you use for your current operator. Keep it."],
      },
    ],
    steps: [
      [
        { text: "Scan the QR code", bold: true },
        " to redirect to the Driver App and sign in as usual. You'll land in your current operator's workspace",
      ],
      [
        "The app recognizes that your account belongs to another operator and offers to move it. ",
        { text: "Tap Transfer driver account", bold: true },
        ".",
      ],
      ["Your account will be automatically detached and transferred to your new Solo Operator. Your previous operator will be notified."],
    ],
    warning: {
      title: "Don't create a second driver account",
      body: [
        "If you sign up fresh instead of transferring, ",
        { text: "you lose your driving history and payout records", underline: true },
        ", and you'll be managing two logins. Transfer keeps everything.",
      ],
    },
    faq: [faqMissingCode],
  },

  "sign-in": {
    id: "sign-in",
    checklist: [
      {
        title: "Your existing Driver App login",
        description: ["Same email and password you've always used. Keep it, you don't need to create a new account."],
      },
    ],
    steps: [
      [{ text: "Scan the QR code", bold: true }, " to redirect to the Driver App and tap ", { text: "Sign in", bold: true }, "."],
      ["Sign in with your usual driver login"],
    ],
    warning: {
      title: "You don't need a second driver email for this",
      body: ["Your driver account already exists, so it comes across as-is."],
    },
    faq: [faqWrongOperator, faqMissingCode],
  },

  register: {
    id: "register",
    checklist: [
      {
        title: "A second email address you can open on your phone",
        description: [`Any address works, Gmail, iCloud, whatever you already use. It just can't be ${OPERATOR_EMAIL_PLACEHOLDER}`],
      },
    ],
    steps: [
      ["Download the driver mobile app via ", { text: "scanning the QR code", bold: true }],
      ["Open the app and tap ", { text: "Register", bold: true }],
      [
        "Sign up with your ",
        { text: "second email address", bold: true },
        ". If you enter your operator login here, the app will stop you.",
      ],
      ["Complete the driver registration"],
    ],
    faq: [faqWrongEmail, faqWrongOperator, faqMissingCode],
  },

  branded: {
    id: "branded",
    intro: [
      [
        "Some companies run their own branded version of RideMinder, with completely separate records. Your driver account in ",
        { text: "COD / Royale", underline: true },
        " isn't visible to the RideMinder Driver App one.",
      ],
      ["You'll be starting fresh here: new RideMinder driver account with new history and private network bookings."],
    ],
    checklist: [
      {
        title: "An email that isn't your Solo Plan login",
        description: [`Any address works, Gmail, iCloud, whatever you already use. It just can't be ${OPERATOR_EMAIL_PLACEHOLDER}`],
      },
    ],
    steps: [
      [
        { text: "Scan the QR code", bold: true },
        " to redirect to the ",
        { text: "RideMinder Driver App", bold: true },
        " — the main one, not your operator's branded app.",
      ],
      ["Tap ", { text: "Register", bold: true }, " and sign up with an email that isn't your Solo Plan login."],
      ["Complete the driver registration"],
    ],
    warning: {
      title: "You can now remove the COD / Royale app whenever you like.",
      body: ["Nothing here depends on it."],
    },
    faq: [faqWrongEmail, faqWrongOperator, faqMissingCode],
  },
};
